// src/hooks/useWorkflow.js
import { useState, useCallback, useRef, useEffect } from 'react';
import {
  startWorkflow as startWorkflowService,
  approveWorkflow as approveWorkflowService,
  rejectWorkflow as rejectWorkflowService
} from '../services/workflowService';

/**
 * Custom hook for managing workflow sessions
 * @returns {Object} Hook return object with workflow state and methods
 */
export const useWorkflow = () => {
  const mountedRef = useRef(true);

  const [state, setState] = useState({
    stage: null,
    category: null,
    icp: null,
    output: null,
    sourcingQueries: null,
    results: null,
    calibrate: null,
    loading: false,
    error: null,
  });

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  /**
   * Reset workflow state
   */
  const resetState = useCallback(() => {
    if (mountedRef.current) {
      setState({
        stage: null,
        category: null,
        icp: null,
        output: null,
        sourcingQueries: null,
        results: null,
        calibrate: null,
        loading: false,
        error: null,
      });
    }
  }, []);

  /**
   * Start a new workflow session
   * @param {string} sessionId - Unique session identifier
   * @param {string} input - User input/intent
   * @returns {Promise<Object>} Workflow response
   */
  const startWorkflow = useCallback(async (sessionId, input) => {
    if (mountedRef.current) {
      setState((prev) => ({ ...prev, loading: true, error: null }));
    }

    try {
      const response = await startWorkflowService(sessionId, input);
      
      if (response.status === 'success' && response.data) {
        const result = response.data;
        if (mountedRef.current) {
          setState({
            stage: result.stage,
            category: result.category || null,
            icp: result.icp || null,
            output: result.output || null,
            sourcingQueries: null,
            results: null,
            calibrate: null,
            loading: false,
            error: null,
          });
        }
        return result;
      } else {
        throw new Error(response.message || 'Failed to start workflow');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to start workflow';
      if (mountedRef.current) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: errorMessage,
        }));
      }
      throw err;
    }
  }, []);

  /**
   * Approve ICP and continue processing
   * @param {string} sessionId - Unique session identifier
   * @returns {Promise<Object>} Workflow response
   */
  const approveWorkflow = useCallback(async (sessionId) => {
    if (mountedRef.current) {
      setState((prev) => ({ ...prev, loading: true, error: null }));
    }

    try {
      const response = await approveWorkflowService(sessionId);
      
      if (response.status === 'success' && response.data) {
        const result = response.data;
        if (mountedRef.current) {
          setState((prev) => ({
            ...prev,
            stage: result.stage,
            sourcingQueries: result.sourcingQueries || null,
            results: result.results || null,
            output: result.output || prev.output,
            loading: false,
            error: null,
          }));
        }
        return result;
      } else {
        throw new Error(response.message || 'Failed to approve workflow');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to approve workflow';
      if (mountedRef.current) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: errorMessage,
        }));
      }
      throw err;
    }
  }, []);

  /**
   * Reject ICP with optional feedback for calibration
   * @param {string} sessionId - Unique session identifier
   * @param {string} [feedback] - Optional feedback for calibration
   * @returns {Promise<Object>} Workflow response
   */
  const rejectWorkflow = useCallback(async (sessionId, feedback) => {
    if (mountedRef.current) {
      setState((prev) => ({ ...prev, loading: true, error: null }));
    }

    try {
      const response = await rejectWorkflowService(sessionId, feedback);
      
      if (response.status === 'success' && response.data) {
        const result = response.data;
        if (mountedRef.current) {
          setState((prev) => ({
            ...prev,
            stage: result.stage,
            calibrate: result.calibrate || null,
            icp: result.calibrate || prev.icp, // Update ICP with calibrated version
            loading: false,
            error: null,
          }));
        }
        return result;
      } else {
        throw new Error(response.message || 'Failed to reject workflow');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to reject workflow';
      if (mountedRef.current) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: errorMessage,
        }));
      }
      throw err;
    }
  }, []);

  // Get current ICP (calibrated or original)
  const currentICP = state.calibrate || state.icp;

  return {
    // State
    stage: state.stage,
    category: state.category,
    icp: currentICP,
    output: state.output,
    sourcingQueries: state.sourcingQueries,
    results: state.results,
    calibrate: state.calibrate,
    loading: state.loading,
    error: state.error,
    
    // Methods
    startWorkflow,
    approveWorkflow,
    rejectWorkflow,
    resetState,
  };
};

export default useWorkflow;
